import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from 'react-bootstrap';
import {
  CircularProgressbar,
  buildStyles
} from 'react-circular-progressbar';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import {  FiCalendar } from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { FiRefreshCw } from 'react-icons/fi';

const OperationDashboard = () => {
  const [operation, setOperation] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("financialYear");
    const [isDateFilterVisible, setIsDateFilterVisible] = useState(false);
    const [customDateRange, setCustomDateRange] = useState({
        startDate: '',
        endDate: ''
    });
  const fetchOperation = async (startDate, endDate) => {
    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/dashboard/operation`, {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });

      setOperation(response.data);
    } catch (error) {
      console.error("Error fetching data:", error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOperation();
  }, []);

  const handleRefresh = () => {
    fetchOperation();
  };

  const totalOrders = 100;
  const ordersInProgress = operation?.orders_in_progress ?? 0;
  const progressPercent1 = totalOrders ? (ordersInProgress / totalOrders) * 100 : 0;
  const totalTasks =
    (operation?.tasks_summary?.in_progress ?? 0) +
    (operation?.tasks_summary?.completed ?? 0) +
    (operation?.tasks_summary?.pending ?? 0);

  const completedPercent = totalTasks ? ((operation?.tasks_summary?.completed / totalTasks) * 100).toFixed(0) : 0;
  const inProgressPercent = totalTasks ? ((operation?.tasks_summary?.in_progress / totalTasks) * 100).toFixed(0) : 0;
  const pendingPercent = totalTasks ? ((operation?.tasks_summary?.pending / totalTasks) * 100).toFixed(0) : 0;

  const taskStatusData = [
    { name: 'Completed', value: operation?.tasks_summary?.completed || 0, color: '#10B981' },
    { name: 'In Progress', value: operation?.tasks_summary?.in_progress || 0, color: '#F59E0B' },
    { name: 'Pending', value: operation?.tasks_summary?.pending || 0, color: '#EF4444' },
  ];
  const getFinancialYear = () => {
    const monthMap = {
      January: '01', February: '02', March: '03', April: '04',
      May: '05', June: '06', July: '07', August: '08',
      September: '09', October: '10', November: '11', December: '12'
    };

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthNum = today.getMonth() + 1;

    try {
      const storedFormData = JSON.parse(localStorage.getItem('formData'));
      
      // 1. Try to get explicit months from settings
      let sMonthName = storedFormData?.start_month;
      let eMonthName = storedFormData?.end_month;

      // 2. Fallback to parsing the financial_year string if months aren't set
      if (!sMonthName || !eMonthName) {
        const fy = storedFormData?.financial_year;
        if (fy && fy.includes('-')) {
          const parts = fy.split('-').map(m => m.trim());
          if (isNaN(parseInt(parts[0]))) {
            sMonthName = parts[0];
            eMonthName = parts[1];
          } else {
            // Case: "2024-2025"
            return {
              startDate: `${parts[0]}-04-01`,
              endDate: `${parts[1]}-03-31`
            };
          }
        }
      }

      if (sMonthName && eMonthName) {
        const sMonth = monthMap[sMonthName];
        const eMonth = monthMap[eMonthName];

        if (sMonth && eMonth) {
          const sMonthInt = parseInt(sMonth);
          // If current month is before start month, started last year
          const startYear = currentMonthNum < sMonthInt ? currentYear - 1 : currentYear;
          const endYear = parseInt(eMonth) < sMonthInt ? startYear + 1 : startYear;
          const endDay = new Date(endYear, parseInt(eMonth), 0).getDate();

          return {
            startDate: `${startYear}-${sMonth}-01`,
            endDate: `${endYear}-${eMonth}-${endDay}`
          };
        }
      }
    } catch (e) {
      console.error("Failed to parse financial year, using fallback:", e);
    }

    // Default Fallback
    return {
      startDate: `${currentYear}-04-01`,
      endDate: `${currentYear + 1}-03-31`
    };
  };

const handleFinancialYearChange = (selectedYear) => {
    if (selectedYear === "financialYear") {
        setSelectedFilter("financialYear");
        setIsDateFilterVisible(false);
        const { startDate, endDate } = getFinancialYear();
        fetchOperation(startDate, endDate);
    } else {
        setSelectedFilter("customRange");
        setIsDateFilterVisible(true);
        setCustomDateRange({ startDate: '', endDate: '' });
    }
};

const handleDateChange = (e) => {
    const { name, value } = e.target;
    setCustomDateRange(prev => ({
        ...prev,
        [name]: value
    }));
};

const handleClearDates = () => {
    setCustomDateRange({ startDate: '', endDate: '' });
    setIsDateFilterVisible(false);
    fetchOperation(null, null);
};

const handleSubmit = () => {
    const { startDate, endDate } = customDateRange;
    if (startDate && endDate) {
        fetchOperation(startDate, endDate);
        setIsDateFilterVisible(false);
    }
};

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">
                  Dashboard
                </h4>
              </div>
            </div>
          </div>
          <div className="dashboard-container">
            <div>
            <div className="d-flex justify-content-end align-items-center mb-3">
                            <DropdownButton
                                        id="financial-year-dropdown"
                                        title={selectedFilter === "financialYear" ? 'This Financial Year' : 'Custom Range'}
                                        onSelect={handleFinancialYearChange}
                                        variant="outline-primary"
                                        className="filter-dropdown"
                                    >
                                        <Dropdown.Item eventKey="financialYear">
                                            <FiCalendar className="me-2" /> This Financial Year
                                        </Dropdown.Item>
                                        <Dropdown.Item eventKey="custom">
                                            <FiCalendar className="me-2" /> Custom Range
                                        </Dropdown.Item>
                                    </DropdownButton>

                                    {isDateFilterVisible && (
                                        <div className="date-range-selector">
                                            <input
                                                type="month"
                                                name="startDate"
                                                value={customDateRange.startDate}
                                                onChange={handleDateChange}
                                                className="form-control date-input"
                                            />
                                            <span className="date-separator">to</span>
                                            <input
                                                type="month"
                                                name="endDate"
                                                value={customDateRange.endDate}
                                                onChange={handleDateChange}
                                                className="form-control date-input"
                                            />
                                            <button
                                                onClick={handleClearDates}
                                                className="btn btn-outline-secondary"
                                            >
                                                Clear
                                            </button>
                                            <button
                                                onClick={handleSubmit}
                                                className="btn btn-primary"
                                                disabled={!customDateRange.startDate || !customDateRange.endDate}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
            <div className="metrics-grid4">
              {/* Orders in Progress Card */}
              <div className="metric-card4">
                <div className="card-header4">
                  <h5>Orders in Progress</h5>
                  <span className="badge bg-warning">Live</span>
                </div>
                <div className="card-content">
                  <div className="metric-value4">{ordersInProgress}</div>
                  <div className="progress-container4">
                    <div className="progress-labels4">
                      <span>Progress</span>
                      <span>{Math.round(progressPercent1)}%</span>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar bg-warning"
                        style={{ width: `${progressPercent1}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Avg Fulfillment Time Card */}
              <div className="metric-card4">
                <div className="card-header4">
                  <h5>Avg Fulfillment Time</h5>
                  <span className="badge bg-info">Trending</span>
                </div>
                <div className="card-content4">
                  <div className="metric-value4">{(operation?.avg_order_fulfillment_time / 60 / 24).toFixed(1)}</div>
                  <div className="metric-label4">days</div>
                  <div className="trend-indicator">
                    <span className="text-success">↓ 12% from last month</span>
                  </div>
                </div>
              </div>

              {/* Task Completion Rate Card */}
              <div className="metric-card4">
                <div className="card-header4">
                  <h5>Completion Rate</h5>
                </div>
                <div className="card-content4">
                  <div className="metric-value4">{completedPercent}%</div>
                  <div className="progress-container4">
                    <div className="progress-labels4">
                      <span>Target: 85%</span>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${completedPercent}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Status Overview Card */}
              <div className="metric-card4">
                <div className="card-header4">
                  <h5>Status Overview</h5>
                </div>
                <div className="card-content4">
                  <div className="progress-circles4">
                    {taskStatusData.map((item, index) => (
                      <div key={index} className="progress-circle-item">
                        <div style={{ width: 50, height: 50 }}>
                          <CircularProgressbar
                            value={(item.value / totalTasks) * 100}
                            text={`${Math.round((item.value / totalTasks) * 100)}%`}
                            styles={buildStyles({
                              textSize: '16px',
                              pathColor: item.color,
                              trailColor: '#E5E7EB',
                              textColor: '#111827',
                            })}
                          />
                        </div>
                        <div className="progress-label4">
                          <span className="status-indicator4" style={{ backgroundColor: item.color }}></span>
                          <span className='font-size-12'>{item.name}</span>
                          <span className="status-count4">{item.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="charts-grid4">
              {/* Order Fulfillment Trend */}
             

              {/* Task Distribution */}
              {/* <div className="chart-card4">
                <div className="card-header4">
                  <h5>Task Distribution</h5>
                </div>
                <div className="chart-container4">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={taskStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label
                      >
                        {taskStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
      </div>

  );
};

export default OperationDashboard;
